<template>
  <figure
    v-if="relatedFabrics.length > 0"
    class="OptionList OptionList--Fabric"
  >
    <figcaption class="OptionList__Label">
      <div class="OptionList__Heading">
        <span class="p4">Fabric</span>
        <span v-if="currentProduct?.label" class="t4">{{
          currentProduct.label
        }}</span>
        <p v-if="currentProduct?.labelDescription" class="p5">
          {{ currentProduct.labelDescription }}
        </p>
      </div>
      <span
        class="p5 SwatchLabel"
        data-type="modal"
        :for="toHandle(terms.modal.fabric)"
        >{{ terms.modal.fabric }}</span
      >
    </figcaption>
    <div class="OptionList__Items">
      <a
        v-for="product in relatedFabrics"
        :key="product.handle"
        :href="product.url"
        class="OptionList__Item"
        data-ht-element="fabric-selector"
        :data-ht-id="toHandle(product.label)"
      >
        <div
          :class="[
            'OptionList__Item-Inner',
            {
              'OptionList__Item-Inner--Active':
                product.handle === currentHandle,
            },
          ]"
        >
          <button class="button button--tertiary-a">{{ product.label }}</button>
        </div>
      </a>
    </div>
  </figure>
</template>

<script setup>
import { computed } from 'vue';
import { useProductStore } from './Composables/Store';
import { relatedProductURL, toHandle } from '@utils/format';

const store = useProductStore();

/**
 * Checks whether the user is in the A/B test variant that enables
 * fabric selector tracking by looking for a body class.
 * @returns {boolean} True if the variant class is present on the body
 */
const isTrackingVariant = () =>
  document.body.classList.contains('js-pdp-fabric-selectors');

const props = defineProps({
  data: { type: Object, required: true, default: () => ({}) },
  terms: { type: Object, required: false, default: () => ({}) },
});

const currentHandle = computed(() => props.data?.item?.handle ?? '');

/**
 * Current selections as [{name, value}], filtered to only active selections.
 * Reactively updates when the user changes any option.
 */
const selectedOptions = computed(() => {
  return store.$state.selected.options.filter(
    (option) => option !== '' && option.name && option.value
  );
});

/**
 * Transforms raw related products metafield data into renderable objects
 * with URLs that deep-link to the best matching variant.
 */
const relatedProducts = computed(() => {
  const nodes = props.data?.item?.relatedProducts?.references?.nodes ?? [];

  return nodes
    .filter((node) => node?.relatedProduct?.reference)
    .map((node) => {
      const product = node.relatedProduct.reference;
      const variants = product.variants?.nodes ?? [];
      const url = relatedProductURL(
        { handle: product.handle, variants },
        selectedOptions.value
      );

      return {
        handle: product.handle,
        label: node.label?.value ?? '',
        labelDescription: node.label_description?.value,
        description: node.description?.value,
        url,
      };
    });
});

/**
 * The active entry from relatedProducts matching the current PDP.
 * Single source of truth for the selected label, description, and fabric handle.
 */
const currentProduct = computed(() => {
  return relatedProducts.value.find(
    (product) => product.handle === currentHandle.value
  );
});

/**
 * Appends fabric selector tracking query parameters to a product URL.
 * Preserves the original `from_fabric` if the user already navigated
 * via the fabric selector, otherwise derives it from the current product.
 * @param {string} baseUrl - The product URL (may already contain ?variant=...)
 * @param {string} viewingFabric - The destination fabric handle
 * @returns {string} URL path + search string with tracking params
 */
const appendTrackingParams = (baseUrl, viewingFabric) => {
  if (!isTrackingVariant()) return baseUrl;

  const url = new URL(baseUrl, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);
  const fabricPreviouslySet = currentParams.get('from_fabric');
  const fromFabric =
    fabricPreviouslySet || toHandle(currentProduct.value?.label ?? '');

  if (!fabricPreviouslySet && fromFabric) {
    currentParams.set('from_fabric', fromFabric);
    const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }

  url.searchParams.set('ref', 'pdp_fabric_selector');
  url.searchParams.set('from_fabric', fromFabric);
  url.searchParams.set('viewing_fabric', viewingFabric);
  return url.pathname + url.search;
};

/**
 * Related products with fabric selector tracking params appended to URLs.
 * Separated from relatedProducts to avoid circular dependency with currentProduct.
 */
const relatedFabrics = computed(() => {
  return relatedProducts.value.map((product) => ({
    ...product,
    url: appendTrackingParams(product.url, toHandle(product.label)),
  }));
});
</script>
